/**
 * free-gift.js — legt das Aktionsgeschenk selbsttätig in den Warenkorb und
 * nimmt es wieder heraus, wenn die Schwelle unterschritten wird.
 *
 * Warum überhaupt: Shopifys automatischer BXGY-Rabatt setzt das Geschenk auf 0 €,
 * sobald es im Warenkorb liegt — er legt es aber NICHT selbst hinein. Genau diese
 * Lücke schließt diese Datei (und macht damit eine kostenpflichtige App überflüssig).
 *
 * Steuerung über Theme-Einstellungen, gerendert durch `snippets/free-gift.liquid`.
 *
 * ── Fassung 2, nach Codex-Prüfung 17.09.2026 (Urteil der ersten Fassung: RISIKO) ──
 * Behobene kritische Fehler der ersten Fassung:
 *  1. HTTP-FEHLER GALTEN ALS ERFOLG. `fetch` wirft bei 422 nicht. Ein ausverkauftes
 *     Geschenk führte zu: add scheitert -> trotzdem refresh -> sync -> add … also
 *     einer Endlosschleife gegen den Shop. Jetzt wird `response.ok` geprüft, ein
 *     Fehlschlag löst KEIN Refresh aus, und nach `MAX_FEHLVERSUCHE` gibt das Skript
 *     für diese Sitzung auf.
 *  2. ZEILENPOSITIONEN KONNTEN DEN FALSCHEN ARTIKEL TREFFEN. `cart/change.js` mit
 *     `line: 3` meint „das dritte Element von jetzt" — entfernt jemand parallel
 *     etwas, verschiebt sich alles und es hätte einen echten Kaufartikel gelöscht.
 *     Jetzt ausschließlich über den stabilen Line-Item-Key (`id`).
 *  3. ÄNDERUNGEN WÄHREND `busy` VERSCHWANDEN. Zwei schnelle Klicks konnten einen
 *     veralteten Warenkorb hinterlassen. Jetzt merkt `dirty` das vor und synct nach.
 *  4. ES WURDE NIE GEPRÜFT, OB DAS GESCHENK WIRKLICH GRATIS IST. Bei abgelaufener
 *     Regel oder abweichender B2B-Berechtigung wären 30 € im Korb geblieben. Jetzt
 *     entscheidet Shopifys Endpreis, nicht meine Nachrechnung.
 * Dazu: alle Geschenkzeilen statt nur der ersten, Opt-out über echte Cart-Daten
 * statt einer innerHTML-Suche, Schutz gegen doppeltes Laden, Timeout gegen ein
 * hängendes `busy`, und Nachstart, wenn der Drawer erst später gerendert wird.
 */
(function () {
  'use strict';

  if (window.__ckFreeGift) return;          // gegen doppeltes Laden (Fund 3)
  window.__ckFreeGift = true;

  var OPT_OUT = 'ck-free-gift-opt-out';
  var MAX_FEHLVERSUCHE = 3;
  var TIMEOUT_MS = 8000;
  var OBSERVER_MAX_MS = 15000;   // danach ist die Aktion offensichtlich nicht aktiv

  var cfg = null;
  var busy = false;
  var dirty = false;
  var fehlversuche = 0;
  var aufgegeben = false;
  // Sperre gegen die Schleife „hinzufügen -> nicht gratis -> entfernen -> hinzufügen".
  // Jeder Durchgang war technisch erfolgreich, der Fehlerzähler griff also nicht;
  // gefunden im zweiten Codex-Lauf 17.09.2026 und durch den eigenen Fix erst
  // entstanden. Schlägt die Gratisprüfung einmal fehl, wird in dieser Sitzung
  // nichts mehr dazugelegt.
  var gratisFehlgeschlagen = false;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /** Konfiguration kommt aus `window.ckFreeGift`, das `layout/theme.liquid`
   *  setzt — NICHT aus dem Snippet. Das Snippet wird nur bei nicht-leerem
   *  Warenkorb gerendert; hing die Konfiguration daran, konnte das Skript beim
   *  ERSTEN Artikel nicht arbeiten, also genau dann nicht, wenn es gebraucht
   *  wird. Im Browser-Test am 17.09.2026 gemessen und daraufhin umgebaut.
   *  Der Rückgriff aufs DOM-Element bleibt als Netz, falls das Layout einmal
   *  ohne die Konfigurationszeile ausgeliefert wird. */
  function readConfig() {
    var g = window.ckFreeGift;
    if (g && g.variantId && g.threshold) {
      return { variantId: parseInt(g.variantId, 10), threshold: parseInt(g.threshold, 10) };
    }
    var el = document.querySelector('free-gift');
    if (!el) return null;
    var variantId = parseInt(el.dataset.variantId, 10);
    var threshold = parseInt(el.dataset.threshold, 10);
    if (!variantId || !threshold) return null;
    return { variantId: variantId, threshold: threshold };
  }

  /** fetch mit Timeout und echter Fehlerprüfung — ein 422 ist KEIN Erfolg (Fund 1).
   *  Der Timer läuft, bis der Body GELESEN ist, nicht nur bis die Header da sind:
   *  sonst könnte `response.json()` hängen, `busy` bliebe für immer true und das
   *  Skript wäre still tot (zweiter Codex-Lauf 17.09.2026). */
  function request(url, options) {
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, TIMEOUT_MS) : null;
    var opts = Object.assign({}, options || {});
    if (ctrl) opts.signal = ctrl.signal;

    function fertig() { if (timer) { clearTimeout(timer); timer = null; } }

    return fetch(url, opts)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (daten) { fertig(); return daten; },
            function (err) { fertig(); throw err; });
  }

  function getCart() {
    return request(root() + 'cart.js', { headers: { Accept: 'application/json' } });
  }

  function post(path, payload) {
    return request(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });
  }

  /** Warenwert OHNE jede Geschenkzeile, vor Rabatt — identisch zur Rabattregel. */
  function qualifyingTotal(cart, variantId) {
    return cart.items.reduce(function (sum, item) {
      return item.variant_id === variantId ? sum : sum + item.original_line_price;
    }, 0);
  }

  /** ALLE Geschenkzeilen (Fund: mehrere Zeilen derselben Variante sind möglich). */
  function giftLines(cart, variantId) {
    return cart.items.filter(function (item) {
      return item.variant_id === variantId;
    }).map(function (item) {
      return { key: item.key, quantity: item.quantity, finalPrice: item.final_line_price };
    });
  }

  /** Immer über den stabilen Key, nie über die Position (Fund 2). */
  function setQuantity(key, quantity) {
    return post('cart/change.js', { id: key, quantity: quantity });
  }

  function refreshCartUI() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function optedOut() {
    try { return sessionStorage.getItem(OPT_OUT) === '1'; } catch (e) { return false; }
  }
  function setOptedOut() {
    try { sessionStorage.setItem(OPT_OUT, '1'); } catch (e) { /* privater Modus */ }
  }

  /** Ein Durchgang. Gibt ein Promise zurück, das immer auflöst. */
  function runSync() {
    return getCart().then(function (cart) {
      var lines = giftLines(cart, cfg.variantId);
      var qualifying = qualifyingTotal(cart, cfg.variantId);
      var reached = qualifying >= cfg.threshold;

      // Mehr als eine Geschenkzeile oder Menge > 1: auf genau eins zurück.
      if (lines.length > 1) {
        return setQuantity(lines[lines.length - 1].key, 0).then(refreshCartUI);
      }
      var gift = lines[0];

      if (!reached && gift) {
        // Schwelle unterschritten — sonst stünde die Seife mit 30 € im Korb.
        return setQuantity(gift.key, 0).then(refreshCartUI);
      }

      if (reached && gift && gift.quantity > 1) {
        return setQuantity(gift.key, 1).then(refreshCartUI);
      }

      if (reached && gift && gift.finalPrice > 0) {
        // Liegt drin, ist aber nicht gratis: Regel abgelaufen, anderer Rabatt,
        // abweichende B2B-Berechtigung. Shopify hat das letzte Wort (Fund 4).
        // Sperren, BEVOR entfernt wird — sonst legt der nächste Durchgang es
        // sofort wieder hinein und das dreht sich endlos.
        gratisFehlgeschlagen = true;
        return setQuantity(gift.key, 0).then(refreshCartUI);
      }

      if (reached && !gift && !optedOut() && !gratisFehlgeschlagen) {
        return post('cart/add.js', { items: [{ id: cfg.variantId, quantity: 1 }] })
          .then(getCart)
          .then(function (after) {
            var added = giftLines(after, cfg.variantId)[0];
            if (!added || added.finalPrice === 0) return null;   // alles gut

            // Nicht gratis — aber WARUM? Ist der Warenwert währenddessen unter die
            // Schwelle gefallen (anderer Tab, parallele Änderung), ist das kein
            // Regelproblem: dann nur herausnehmen, NICHT dauerhaft sperren. Sonst
            // bliebe die Aktion für diese Kundin bis zum Neuladen still aus
            // (dritter Codex-Lauf, mit Repro belegt).
            if (qualifyingTotal(after, cfg.variantId) >= cfg.threshold) {
              gratisFehlgeschlagen = true;
            }
            return setQuantity(added.key, 0);
          })
          .then(refreshCartUI);
      }
      return null;
    });
  }

  function sync() {
    if (aufgegeben || !cfg) return;
    if (busy) { dirty = true; return; }     // nichts verwerfen (Fund 3)
    busy = true;
    dirty = false;

    runSync().then(function () {
      fehlversuche = 0;
    }, function () {
      // Kein Refresh im Fehlerfall — sonst dreht sich die Schleife weiter (Fund 1).
      fehlversuche += 1;
      if (fehlversuche >= MAX_FEHLVERSUCHE) aufgegeben = true;
    }).then(function () {
      busy = false;
      if (dirty && !aufgegeben) setTimeout(sync, 80);
    });
  }

  /** Opt-out SOFORT beim Klick setzen, aus den Daten im DOM.
   *  Die erste Fassung fragte den Warenkorb zweimal ab und entschied nach 700 ms —
   *  da hatte der Sync (80 ms) das Geschenk längst wieder hineingelegt, das Opt-out
   *  verlor das Rennen (zweiter Codex-Lauf). Der Line-Item-Key steht im Markup und
   *  beginnt mit der Varianten-ID, also ist die Zuordnung synchron möglich. */
  /** Ist dieser Line-Item-Key unser Geschenk? Der Key hat die Form
   *  "<variant_id>:<hash>" — ein reiner Präfixvergleich würde auch
   *  "530784447040081" treffen, also eine FREMDE Variante mit angehängter
   *  Ziffer (dritter Codex-Lauf, mit Repro belegt). Deshalb exakt bis zum ":". */
  function istGeschenkKey(key) {
    if (!key || !cfg) return false;
    return String(key).split(':')[0] === String(cfg.variantId);
  }

  function watchManualRemoval() {
    document.addEventListener('click', function (e) {
      if (!cfg || !e.target.closest) return;
      // NUR das Löschen zählt als Ablehnung. Ein Minus-Klick kann auch 2 -> 1
      // meinen; das als „will ich nicht" zu werten, schaltete die Aktion still
      // ab (dritter Codex-Lauf).
      var ziel = e.target.closest('.remove');
      if (!ziel) return;
      var zeile = ziel.closest('[data-product-id], [id^="CartItem-"], [id^="CartDrawerItem-"], .product-cart-item');
      if (!zeile) return;
      var traeger = zeile.querySelector('[data-id]') || zeile;
      var key = traeger.dataset && (traeger.dataset.id || traeger.dataset.productId);
      if (istGeschenkKey(key)) setOptedOut();
    }, true);
  }

  /** Nach fremden Cart-Mutationen neu prüfen. Antwort unverändert durchreichen. */
  function wrapFetch() {
    var orig = window.fetch;
    window.fetch = function (input) {
      var href = typeof input === 'string' ? input
        : (input && (input.url || (input.href && String(input)))) || '';
      var istMutation = /\/cart\/(add|change|update|clear)/.test(String(href));
      return orig.apply(this, arguments).then(function (response) {
        if (istMutation) setTimeout(sync, 80);
        return response;
      });
    };
  }

  function boot() {
    cfg = readConfig();
    watchManualRemoval();
    wrapFetch();
    document.addEventListener('cart:refresh', function () { setTimeout(sync, 80); });
    document.addEventListener('line-item:change:end', function () { setTimeout(sync, 80); });

    // Der Drawer wird nachgerendert: Konfiguration nachziehen, wenn sie beim
    // Start noch fehlte, sonst bliebe das Skript für immer stumm (Fund).
    // Der Beobachter hängt sich ab, sobald er die Konfiguration hat — und
    // spätestens nach OBSERVER_MAX_MS. Ohne dieses Zeitfenster liefe er bei
    // ABGESCHALTETER Aktion dauerhaft mit: das Snippet rendert dann gar kein
    // <free-gift>, er würde also ewig weitersuchen (dritter Codex-Lauf).
    if (!cfg && typeof MutationObserver !== 'undefined') {
      var beobachter = new MutationObserver(function () {
        cfg = readConfig();
        if (cfg) { beobachter.disconnect(); sync(); }
      });
      beobachter.observe(document.body || document.documentElement,
                         { childList: true, subtree: true });
      setTimeout(function () { beobachter.disconnect(); }, OBSERVER_MAX_MS);
    }
    if (cfg) sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
